// Single implementation of time-zone offset and wall-clock resolution (DST-aware, Intl only).
const formatters = new Map<string, Intl.DateTimeFormat>();
const ISO_DOW: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};
const DAY_MS = 86_400_000;

export type ZoneParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** ISO weekday, 1 = Monday … 7 = Sunday. */
  weekday: number;
};

function formatter(tz: string): Intl.DateTimeFormat {
  const cached = formatters.get(tz);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatters.set(tz, created);
  return created;
}

/** Wall-clock parts of an instant in `tz`. */
export function zoneParts(instant: Date, tz: string): ZoneParts {
  const p: Record<string, string> = {};
  for (const part of formatter(tz).formatToParts(instant))
    p[part.type] = part.value;
  return {
    year: +p.year,
    month: +p.month,
    day: +p.day,
    hour: +p.hour,
    minute: +p.minute,
    second: +p.second,
    weekday: ISO_DOW[p.weekday],
  };
}

/** Offset of `tz` from UTC at `instant`, in milliseconds (east positive). */
export function zoneOffsetMs(instant: Date, tz: string): number {
  const p = zoneParts(instant, tz);
  const asUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * UTC instant of a wall-clock time in `tz` ("compatible" rule): an ambiguous
 * time (fall back) resolves to the first occurrence; a nonexistent time
 * (spring forward) resolves to the later instant, i.e. shifted past the gap.
 * `wallUtcMs` is the wall time written as if it were UTC.
 */
export function wallToUtc(wallUtcMs: number, tz: string): Date {
  const before = zoneOffsetMs(new Date(wallUtcMs - DAY_MS), tz);
  const after = zoneOffsetMs(new Date(wallUtcMs + DAY_MS), tz);
  const valid = [...new Set([before, after])]
    .map((off) => wallUtcMs - off)
    .filter((utc) => zoneOffsetMs(new Date(utc), tz) === wallUtcMs - utc);
  // No valid candidate means the wall time falls in a gap: use the pre-gap offset.
  return new Date(valid.length ? Math.min(...valid) : wallUtcMs - before);
}
